import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Select,
  Input,
  Tag,
  Button,
  Modal,
  Space,
  Tooltip,
  Avatar,
  Form,
  message,
  Card,
  Row,
  Col,
  Alert,
  Popover,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  WhatsAppOutlined,
  PhoneOutlined,
  LineOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  AppstoreOutlined,
  CopyOutlined,
  KeyOutlined,
  LinkOutlined,
  InfoCircleOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import api from "../../../services/api";
import { showBasicAlert } from "../../../core/common/useAlert";
import CommonSelect from "../../../core/common/commonSelect";
import { useSelector } from "react-redux";
import Datatable from "../../../core/common/dataTable";
import ConfirmationModal from "../../ers/common/components/ConfirmationModal";

const { Option } = Select;

interface WhatsAppAccountRecord {
  id: number;
  company_id: number;
  company_type: string;
  phone_number_id: string;
  display_phone_number: string;
  business_name: string;
  status: number;
  app_id: string | null;
  webhook_verify_token: string | null;
  createdAt: string;
  updatedAt: string;
}

const WhatsAppAccounts = () => {
  const user = useSelector((state: any) => state.user.user);
  const signature = localStorage.getItem("signature");
  const routes = all_routes;
  const navigate = useNavigate();
  const location = useLocation();

  // State variables
  const [accounts, setAccounts] = useState<WhatsAppAccountRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState<any[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedAccount, setSelectedAccount] =
    useState<WhatsAppAccountRecord | null>(null);
  const [form] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<number | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const webhookBaseUrl = `${process.env.REACT_APP_API_URL}leadService/webhook/whatsapp`;

  // Company state
  const [formDataCompanyChoose, setformDataCompanyChoose] = useState({
    signature: signature,
    companyName: "Select Company",
    companyId: "",
    companyType: "",
  });

  // Fetch companies
  const fetchCompanies = async () => {
    try {
      const getAllCompanyList = await api.post(
        "userService/getAllCompanyList",
        {
          signature,
        }
      );
      setCompanies(getAllCompanyList?.data?.data?.data || []);
    } catch (err: any) {
      showBasicAlert(
        err?.response?.data?.message?.[0]?.msg || "Error fetching companies"
      );
      console.error("Error fetching companies:", err);
    }
  };

  // Fetch WhatsApp accounts
  const fetchWhatsAppAccounts = async () => {
    try {
      if (
        !formDataCompanyChoose?.companyId ||
        !formDataCompanyChoose?.companyType
      ) {
        return;
      }

      setLoading(true);
      const response = await api.post(
        "leadService/getWhatsappBusinessAccounts",
        {
          signature,
          company_id: formDataCompanyChoose.companyId,
          company_type: formDataCompanyChoose.companyType,
        }
      );

      if (response.data?.message?.[0]?.status) {
        setAccounts(response.data.message[0].data || []);
      }
    } catch (err: any) {
      showBasicAlert(
        err?.response?.data?.message?.[0]?.msg ||
          "Error fetching WhatsApp accounts"
      );
      console.error("Error fetching WhatsApp accounts:", err);
    } finally {
      setLoading(false);
    }
  };

  // Company options
  const companyChoose = companies.map((company: any) => ({
    value: company.id.toString(),
    label: `${company.name} - ${company.type}`,
    companyName: company.name,
  }));

  // Handle company change
  const handleCompanyChange = async (selectedOption: any) => {
    if (!selectedOption) return;
    const newFormData = {
      companyId: selectedOption.value,
      companyName:
        selectedOption.label.split("-")[0]?.trim() || "All Companies",
      companyType: selectedOption.label.split("-")[1]?.trim() || "WhatsApp",
    };

    setformDataCompanyChoose((prev: any) => ({
      ...prev,
      ...newFormData,
    }));
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (
      formDataCompanyChoose?.companyId &&
      formDataCompanyChoose?.companyType
    ) {
      fetchWhatsAppAccounts();
    }
  }, [formDataCompanyChoose]);

  useEffect(() => {
    if (companies.length) {
      handleCompanyChange({
        value: companies[0].id.toString(),
        label: companies[0].name + " - " + companies[0].type,
      });
    }
  }, [companies]);

  // Handle register account
  const handleRegisterAccount = async (values: any) => {
    try {
      const payload = {
        ...values,
        signature,
        company_id: formDataCompanyChoose.companyId,
        company_type: formDataCompanyChoose.companyType,
      };

      const response = await api.post(
        "leadService/registerWhatsappAccount",
        payload
      );

      if (response.data?.message?.[0]?.status) {
        showBasicAlert(
          response.data.message[0].msg || "Account registered successfully"
        );
        setIsModalVisible(false);
        form.resetFields();
        fetchWhatsAppAccounts();
      } else {
        showBasicAlert(
          response.data?.message?.[0]?.msg || "Registration failed"
        );
      }
    } catch (err: any) {
      showBasicAlert(
        err?.response?.data?.message?.[0]?.msg || "Error registering account"
      );
      console.error("Error registering account:", err);
    }
  };

  // Handle edit account
  const handleEdit = (account: WhatsAppAccountRecord) => {
    setIsEditMode(true);
    setSelectedAccount(account);
    form.setFieldsValue({
      business_name: account.business_name,
      phone_number_id: account.phone_number_id,
      display_phone_number: account.display_phone_number,
      app_id: account.app_id,
    });
    setIsModalVisible(true);
  };

  // Handle update account
  const handleUpdateAccount = async (values: any) => {
    if (!selectedAccount) return;

    try {
      const payload = {
        ...values,
        signature,
        id: selectedAccount.id,
        company_id: formDataCompanyChoose.companyId,
        company_type: formDataCompanyChoose.companyType,
      };

      const response = await api.post(
        "leadService/updateWhatsappAccount",
        payload
      );

      if (response.data?.message?.[0]?.status) {
        showBasicAlert(
          response.data.message[0].msg || "Account updated successfully"
        );
        setIsModalVisible(false);
        setIsEditMode(false);
        setSelectedAccount(null);
        form.resetFields();
        fetchWhatsAppAccounts();
      } else {
        showBasicAlert(response.data?.message?.[0]?.msg || "Update failed");
      }
    } catch (err: any) {
      showBasicAlert(
        err?.response?.data?.message?.[0]?.msg || "Error updating account"
      );
      console.error("Error updating account:", err);
    }
  };

  // Handle delete account
  const handleDelete = async (id: number | null) => {
    try {
      const response = await api.post("leadService/deleteWhatsappAccount", {
        signature,
        id,
        company_id: formDataCompanyChoose.companyId,
        company_type: formDataCompanyChoose.companyType,
      });

      if (response.data?.message?.[0]?.status) {
        showBasicAlert(
          response.data.message[0].msg || "Account deactivated successfully"
        );
        fetchWhatsAppAccounts();
      } else {
        showBasicAlert(
          response.data?.message?.[0]?.msg || "Deactivation failed"
        );
      }
    } catch (err: any) {
      showBasicAlert(
        err?.response?.data?.message?.[0]?.msg || "Error deactivating account"
      );
      console.error("Error deactivating account:", err);
    }
  };

  const handleDeleteModalOpen = useCallback((id: number) => {
    setDeleteId(id);
    setIsDeleteModalOpen(true);
  }, []);
  const handleCloseModal = useCallback(() => {
    setIsDeleteModalOpen(false);
  }, []);

  const handleView = (record: WhatsAppAccountRecord) => {
    navigate(`${routes.whatsAppLeadsChat}${location?.search}`, {
      state: {
        accountId: record.id,
        phoneNumberId: record.phone_number_id,
        businessName: record.business_name,
        displayPhoneNumber: record.display_phone_number,
        companyId: formDataCompanyChoose.companyId,
        companyType: formDataCompanyChoose.companyType,
      },
    });
  };

  // Status mapping
  const getStatus = (status: number) => {
    switch (status) {
      case 1:
        return {
          text: "Active",
          color: "green",
          icon: <CheckCircleOutlined />,
        };
      case 0:
        return {
          text: "Inactive",
          color: "red",
          icon: <CloseCircleOutlined />,
        };
      default:
        return { text: "Unknown", color: "default", icon: <UserOutlined /> };
    }
  };

  // Filter accounts
  const filteredAccounts = accounts.filter((account) => {
    const matchesSearch =
      searchTerm === "" ||
      account.business_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.display_phone_number.includes(searchTerm) ||
      account.phone_number_id.includes(searchTerm);

    const matchesCompany =
      selectedCompany === null ||
      account.company_id.toString() === selectedCompany;

    const matchesStatus =
      selectedStatus === null || account.status === selectedStatus;

    return matchesSearch && matchesCompany && matchesStatus;
  });

  // Clear filters
  const handleClearFilters = () => {
    setSearchTerm("");
    setSelectedCompany(null);
    setSelectedStatus(null);
  };

  // Table columns
  const columns: ColumnsType<WhatsAppAccountRecord> = [
    {
      title: "Business Name",
      dataIndex: "business_name",
      key: "business_name",
      render: (text: string, record) => (
        <div className="d-flex align-items-center">
          <div className="me-3">
            <Avatar
              size={40}
              style={{
                backgroundColor: "#25D366",
                fontSize: "18px",
                fontWeight: "600",
              }}
              icon={<WhatsAppOutlined />}
            />
          </div>
          <div>
            <h6 className="mb-0 fw-semibold">{text}</h6>
          </div>
        </div>
      ),
      sorter: (a, b) => a.business_name.localeCompare(b.business_name),
    },
    {
      title: "Display Phone No",
      key: "phone_details",
      render: (_, record) => (
        <span className="ms-2">{record.display_phone_number}</span>
      ),
    },
    {
      title: "Phone No ID",
      key: "phone_details",
      render: (_, record) => (
        <span className="ms-2">{record.phone_number_id}</span>
      ),
    },
    {
      title: "Web Hook URL",
      key: "webhook",
      width: 300,
      render: (_, record) => {
        // Generate webhook URL
        const webhookUrl = record.app_id
          ? `${webhookBaseUrl}?app_id=${record.app_id}`
          : webhookBaseUrl;

        return (
          <div className="webhook-config-column">
            {/* Webhook URL Section */}
            <div className="mb-2">
              <div className="d-flex align-items-center">
                <LinkOutlined
                  className="me-2 text-primary"
                  style={{ fontSize: "12px" }}
                />
                <div className="d-flex align-items-center flex-grow-1">
                  <span className="text-truncate" style={{ maxWidth: "250px" }}>
                    {webhookUrl.replace("https://", "")}
                  </span>
                  <Tooltip title="Copy Webhook URL">
                    <Button
                      type="text"
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(webhookUrl);
                        message.success("Webhook URL copied!");
                      }}
                      style={{ marginLeft: "4px", padding: "0 4px" }}
                    />
                  </Tooltip>
                </div>
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: "Web Hook Token",
      key: "webhook",
      width: 200,
      render: (_, record) => {
        // Generate webhook URL
        const webhookUrl = record.app_id
          ? `${webhookBaseUrl}?app_id=${record.app_id}`
          : webhookBaseUrl;

        return (
          <div className="webhook-config-column">
            {/* Verify Token Section */}
            <div>
              <div className="d-flex align-items-center">
                {record.webhook_verify_token ? (
                  <div className="d-flex align-items-center">
                    <Tag color="green">
                      <span
                        className="text-truncate"
                        style={{ width: "150px" }}>
                        {record.webhook_verify_token}
                      </span>
                    </Tag>
                    <Tooltip title="Copy Verify Token">
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(
                            record.webhook_verify_token || ""
                          );
                          message.success("Verify token copied!");
                        }}
                        style={{ marginLeft: "4px", padding: "0 4px" }}
                      />
                    </Tooltip>
                  </div>
                ) : (
                  <Tag color="orange">Not set</Tag>
                )}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: number) => {
        const statusData = getStatus(status);
        return (
          <Tag color={statusData.color} icon={statusData.icon}>
            {statusData.text}
          </Tag>
        );
      },
      filters: [
        { text: "Active", value: 1 },
        { text: "Inactive", value: 0 },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Actions",
      key: "actions",
      width: "120px",
      render: (_, record) => (
        <Space>
          <Tooltip title="View">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
              disabled={record.status === 0}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              disabled={record.status === 0}
            />
          </Tooltip>
          <Tooltip
            title={record.status === 1 ? "Deactivate" : "Already Inactive"}>
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteModalOpen(record.id)}
              disabled={record.status === 0}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Add CSS for better display
  const tableStyles = `
    .webhook-config-column {
      font-size: 12px;
    }
    .webhook-config-column .ant-tag {
      font-size: 10px;
      padding: 0 6px;
    }
    .webhook-config-column .ant-btn-link {
      font-size: 11px;
      padding: 0 4px;
    }
  `;

  return (
    <>
      <style>{tableStyles}</style>

      <div className="page-wrapper">
        <div className="content">
          {/* Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">WhatsApp Business Accounts</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Business</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    WhatsApp Accounts
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="dropdown me-2 mb-2">
                <CommonSelect
                  className="select"
                  options={companyChoose}
                  defaultValue={{
                    value: `${formDataCompanyChoose.companyId}-${formDataCompanyChoose.companyType}`,
                    label:
                      formDataCompanyChoose.companyName +
                      "-" +
                      formDataCompanyChoose.companyType,
                  }}
                  onChange={handleCompanyChange}
                />
              </div>
              <div className="mb-2">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setIsEditMode(false);
                    setSelectedAccount(null);
                    form.resetFields();
                    setIsModalVisible(true);
                  }}
                  className="d-flex align-items-center">
                  Add Account
                </Button>
              </div>
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>

          {/* Stats Cards - Updated with Webhook Stats */}
          <div className="row">
            <div className="col-xl-3 col-md-6 d-flex">
              <div className="card flex-fill">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h6 className="text-muted mb-1">Total Accounts</h6>
                      <h4 className="mb-0">{accounts.length}</h4>
                    </div>
                    <div className="avatar avatar-rounded bg-primary-transparent">
                      <WhatsAppOutlined className="fs-20 text-primary" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-md-6 d-flex">
              <div className="card flex-fill">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h6 className="text-muted mb-1">Webhook Ready</h6>
                      <h4 className="mb-0">
                        {
                          accounts.filter(
                            (a) => a.app_id && a.webhook_verify_token
                          ).length
                        }
                      </h4>
                    </div>
                    <div className="avatar avatar-rounded bg-success-transparent">
                      <CheckCircleOutlined className="fs-20 text-success" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-md-6 d-flex">
              <div className="card flex-fill">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h6 className="text-muted mb-1">Needs Setup</h6>
                      <h4 className="mb-0">
                        {
                          accounts.filter(
                            (a) => !a.app_id || !a.webhook_verify_token
                          ).length
                        }
                      </h4>
                    </div>
                    <div className="avatar avatar-rounded bg-warning-transparent">
                      <SettingOutlined className="fs-20 text-warning" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-md-6 d-flex">
              <div className="card flex-fill">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h6 className="text-muted mb-1">Active</h6>
                      <h4 className="mb-0">
                        {accounts.filter((a) => a.status === 1).length}
                      </h4>
                    </div>
                    <div className="avatar avatar-rounded bg-info-transparent">
                      <LineOutlined className="fs-20 text-info" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Accounts Table */}
          <div className="card">
            <div className="card-header">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <h5 className="card-title mb-0">WhatsApp Business Accounts</h5>
                <div className="d-flex gap-2 flex-wrap">
                  <Input
                    placeholder="Search by name, phone..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ width: 200 }}
                    allowClear
                  />
                  <Select
                    placeholder="Status"
                    value={selectedStatus}
                    onChange={setSelectedStatus}
                    style={{ width: 130 }}
                    allowClear>
                    <Option value={null}>All Status</Option>
                    <Option value={1}>Active</Option>
                    <Option value={0}>Inactive</Option>
                  </Select>
                  {(searchTerm ||
                    selectedCompany !== null ||
                    selectedStatus !== null) && (
                    <Button onClick={handleClearFilters}>Clear Filters</Button>
                  )}
                </div>
              </div>
            </div>
            <div className="card-body p-0">
              <Datatable columns={columns} dataSource={filteredAccounts} />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2025 © CMS.</p>
          <p>
            Designed & Developed By{" "}
            <Link
              to="#"
              onClick={(e: any) => e.preventDefault()}
              className="text-primary">
              ZYVO
            </Link>
          </p>
        </div>
      </div>

      {/* Add/Edit Account Modal */}
      <Modal
        title={
          <div className="d-flex align-items-center">
            <div
              className="d-flex align-items-center justify-content-center me-3"
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "10px",
                backgroundColor: isEditMode ? "#fff7e6" : "#e6f7ff",
              }}>
              {isEditMode ? (
                <EditOutlined style={{ fontSize: "20px", color: "#fa8c16" }} />
              ) : (
                <PlusOutlined style={{ fontSize: "20px", color: "#1890ff" }} />
              )}
            </div>
            <div>
              <h5 className="mb-0">
                {isEditMode ? "Edit Account" : "Add New Account"}
              </h5>
              <small className="text-muted">
                {isEditMode
                  ? "Update WhatsApp business account details"
                  : "Register new WhatsApp business account"}
              </small>
            </div>
          </div>
        }
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setIsEditMode(false);
          setSelectedAccount(null);
          form.resetFields();
        }}
        footer={null}
        width={600}>
        <Form
          form={form}
          layout="vertical"
          onFinish={isEditMode ? handleUpdateAccount : handleRegisterAccount}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Phone Number ID"
                name="phone_number_id"
                rules={[
                  { required: true, message: "Please enter Phone Number ID" },
                  {
                    min: 5,
                    message: "Phone Number ID must be at least 5 characters",
                  },
                ]}>
                <Input
                  placeholder="Enter WhatsApp Phone Number ID"
                  prefix={<PhoneOutlined />}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Display Phone Number"
                name="display_phone_number"
                rules={[
                  {
                    required: true,
                    message: "Please enter Display Phone Number",
                  },
                ]}>
                <Input placeholder="1234567890" prefix={<WhatsAppOutlined />} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Business Name"
            name="business_name"
            rules={[
              { required: true, message: "Please enter Business Name" },
              {
                min: 2,
                message: "Business Name must be at least 2 characters",
              },
            ]}>
            <Input
              placeholder="Enter business name"
              prefix={<LineOutlined />}
            />
          </Form.Item>

          <Form.Item
            label="Meta App ID"
            name="app_id"
            tooltip="The Facebook/Meta Developer App ID for this WhatsApp number">
            <Input
              placeholder="e.g., 123456789012345"
              prefix={<AppstoreOutlined />}
              allowClear
            />
          </Form.Item>

          {isEditMode && (
            <Alert
              message="Webhook Configuration"
              description={
                <div>
                  <p>
                    <strong>Webhook URL:</strong> {webhookBaseUrl}
                  </p>
                  <p>
                    <strong>Token:</strong>{" "}
                    {selectedAccount?.webhook_verify_token ||
                      "Will be auto-generated"}
                  </p>
                  <p className="text-muted">
                    Copy these values to Meta Developer Portal to configure
                    webhook.
                  </p>
                </div>
              }
              type="info"
              showIcon
            />
          )}

          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button
              onClick={() => {
                setIsModalVisible(false);
                setIsEditMode(false);
                setSelectedAccount(null);
                form.resetFields();
              }}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">
              {isEditMode ? "Update Account" : "Register Account"}
            </Button>
          </div>
        </Form>
      </Modal>
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseModal}
        message="Are you sure you want to delete this account?"
        title="Delete Account"
        onConfirm={() => handleDelete(deleteId)}
        type="danger"
      />
    </>
  );
};

export default WhatsAppAccounts;
